/* ==========================================================================
   assets/svamfirst-popup.js
   ==========================================================================
   Handles the entire 4-step SVAMFirst popup:
     - Opening / closing the modal
     - Step-by-step validation (controls when a form is allowed to submit)
     - Building the customer Notes text
     - Sending collected data + tag + note to your backend endpoint,
       which is responsible for calling the Shopify Admin API to
       create/update the customer, add the "svamfirst" tag, and write
       the Notes field.

   WHY A BACKEND CALL IS NEEDED:
   Shopify's Admin API (customer create/update, tags, notes) requires a
   private Admin API access token. That token can never be placed in
   theme JS/browser code — it would be visible to anyone. So this file
   POSTs a JSON payload to the endpoint set in the section settings
   ("API Endpoint URL"), and YOUR server/app on that endpoint performs
   the actual Admin API call using the token safely, server-side.

   Payload shape sent on every save:
   {
     "step": "maybe_next_time" | "full_profile",
     "name": "...", "city": "...", "email": "...", "phone": "...",
     "products_interested": ["Lip Crayon", "Kajal"],
     "skin_tone": "Medium",              // only on full_profile
     "undertone": "Warm-Neutral",        // only on full_profile
     "preferred_shades": ["Brown","Red"],// only on full_profile
     "tag": "svamfirst",
     "note": "ready-to-save note text"
   }
   ========================================================================== */

(function () {
  document.addEventListener('DOMContentLoaded', function () {
    var overlay = document.getElementById('svamfirst-modal-overlay');
    if (!overlay) return;

    var endpoint = overlay.getAttribute('data-svamfirst-endpoint');
    var loading  = overlay.querySelector('[data-svamfirst-loading]');
    var steps    = overlay.querySelectorAll('.svamfirst-step');
    var form1    = document.getElementById('svamfirst-form-step1');
    var form3    = document.getElementById('svamfirst-form-step3');
    var skinToneLabels = ['Fair', 'Light', 'Medium', 'Tan', 'Deep'];

    // Holds data collected across steps for the current popup session
    var collected = {};

    function showStep(n) {
      steps.forEach(function (s) {
        s.hidden = s.getAttribute('data-step') != n;
      });
    }

    function openModal() {
      collected = {};
      if (form1) form1.reset();
      if (form3) form3.reset();
      showStep(1);
      overlay.classList.add('is-active');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      overlay.classList.remove('is-active');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    document.querySelectorAll('[data-svamfirst-trigger]').forEach(function (btn) {
      btn.addEventListener('click', openModal);
    });
    overlay.querySelectorAll('[data-svamfirst-close]').forEach(function (btn) {
      btn.addEventListener('click', closeModal);
    });
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeModal();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-active')) closeModal();
    });

    function getCheckedValues(form, name) {
      return Array.prototype.slice
        .call(form.querySelectorAll('input[name="' + name + '"]:checked'))
        .map(function (el) { return el.value; });
    }

    function buildNoteText(data) {
      var lines = [];
      lines.push('--- SVAMFirst Signup ---');
      if (data.name) lines.push('Name: ' + data.name);
      if (data.city) lines.push('City: ' + data.city);
      if (data.email) lines.push('Email: ' + data.email);
      if (data.phone) lines.push('Phone: ' + data.phone);
      if (data.products_interested && data.products_interested.length) {
        lines.push('Products Interested In: ' + data.products_interested.join(', '));
      }
      if (data.skin_tone) lines.push('Skin Tone: ' + data.skin_tone);
      if (data.undertone) lines.push('Undertone: ' + data.undertone);
      if (data.preferred_shades && data.preferred_shades.length) {
        lines.push('Preferred Shades: ' + data.preferred_shades.join(', '));
      }
      return lines.join('\n');
    }

    function setFormDisabled(form, disabled) {
      if (!form) return;
      var btn = form.querySelector('button[type="submit"]');
      if (btn) btn.disabled = disabled;
    }

    function saveToShopify(payload) {
      if (!endpoint) {
        console.warn('SVAMFirst: "API Endpoint URL" not set in section settings. Payload not sent:', payload);
        return Promise.resolve();
      }
      if (loading) loading.hidden = false;
      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Save failed with status ' + res.status);
          return res.json().catch(function () { return {}; });
        })
        .catch(function (err) {
          console.error('SVAMFirst save failed:', err);
          alert('Something went wrong while saving your details. Please try again.');
          return Promise.reject(err);
        })
        .finally(function () {
          if (loading) loading.hidden = true;
        });
    }

    // ---------------- STEP 1 : Name + Email required ----------------
    if (form1) {
      form1.addEventListener('submit', function (e) {
        e.preventDefault();

        var name  = form1.name.value.trim();
        var email = form1.email.value.trim();
        var emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

        if (!name) {
          alert('Please enter your name.');
          return; // submit blocked
        }
        if (!email || !emailValid) {
          alert('Please enter a valid email address.');
          return; // submit blocked
        }

        collected.name  = name;
        collected.city  = form1.city.value.trim();
        collected.email = email;
        collected.phone = form1.phone.value.trim();
        collected.products_interested = getCheckedValues(form1, 'products');

        showStep(2); // validation passed -> move forward
      });
    }

    // ---------------- STEP 2 : Maybe Next Time ----------------
    overlay.querySelectorAll('[data-svamfirst-maybe-later]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var payload = Object.assign({}, collected, {
          step: 'maybe_next_time',
          tag: 'svamfirst',
          note: buildNoteText(collected)
        });
        btn.disabled = true;
        saveToShopify(payload)
          .then(closeModal)
          .finally(function () { btn.disabled = false; });
      });
    });

    // ---------------- STEP 2 : Find My Comfort ----------------
    overlay.querySelectorAll('[data-svamfirst-find-comfort]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        showStep(3);
      });
    });

    // ---------------- STEP 3 : Submit (needs undertone selected) ----------------
    if (form3) {
      form3.addEventListener('submit', function (e) {
        e.preventDefault();

        var undertoneEl = form3.querySelector('input[name="undertone"]:checked');
        if (!undertoneEl) {
          alert('Please choose your skin\'s undertone.');
          return; // submit blocked
        }

        var rangeVal = parseInt(form3.skin_tone_range.value, 10);
        collected.skin_tone = skinToneLabels[rangeVal] || '';
        collected.undertone = undertoneEl.value;
        collected.preferred_shades = getCheckedValues(form3, 'shades');

        var payload = Object.assign({}, collected, {
          step: 'full_profile',
          tag: 'svamfirst',
          note: buildNoteText(collected) // step 1 + step 3 combined
        });

        setFormDisabled(form3, true);
        saveToShopify(payload)
          .then(function () {
            showStep(4); // only advance after successful save
          })
          .finally(function () {
            setFormDisabled(form3, false);
          });
      });
    }

    // ---------------- STEP 4 : Continue Shopping ----------------
    overlay.querySelectorAll('[data-svamfirst-continue]').forEach(function (btn) {
      btn.addEventListener('click', closeModal);
    });

    // ---------------- Optional auto-open (set by the section) ----------------
    if (window.svamfirstAutoOpenDelay) {
      setTimeout(openModal, window.svamfirstAutoOpenDelay);
    }
  });
})();
